'use client'

import { Button } from '#/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '#/components/ui/sheet'
import { Link, useRouterState } from '@tanstack/react-router'
import { Menu } from 'lucide-react'
import { useState } from 'react'
import Container from './Container'
import OrderOnlineButton from './OrderOnlineButton'

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/menu', label: 'Menu' },
  { to: '/services', label: 'Services' },
  { to: '/blog', label: 'Blog' },
  { to: '/contact', label: 'Contact' },
] as const

export default function Header({ orderUrl }: { orderUrl?: string }) {
  const [open, setOpen] = useState(false)
  const routerState = useRouterState()
  const currentPath = routerState.location.pathname

  function isActive(to: string) {
    if (to === '/') return currentPath === '/'
    return currentPath.startsWith(to)
  }

  return (
    <header
      className="sticky top-0 z-50 bg-surface-lowest/95 backdrop-blur-xl"
      style={{
        borderTop: '2px solid var(--primary)',
        borderBottom: '6px double var(--primary)',
        boxShadow: 'var(--shadow-ambient)',
      }}
    >
      <Container>
        <nav className="flex items-center justify-between py-8 sm:py-10">
          {/* Logo */}
          <Link to="/" className="no-underline">
            <img
              src="/images/logo.svg"
              alt="Main Street Bread"
              className="h-20 w-auto sm:h-28"
            />
          </Link>

          {/* Right side: nav links + Order Online + mobile menu */}
          <div className="flex items-center gap-3 sm:gap-5">
          {/* Desktop nav */}
          <div className="hidden items-stretch self-stretch divide-x divide-primary/30 border-x border-primary/30 md:flex">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`relative flex items-center px-5 text-base font-medium no-underline transition-colors ${isActive(link.to)
                    ? 'text-primary'
                    : 'text-on-surface-variant hover:bg-surface-high hover:text-on-surface'
                  }`}
              >
                {link.label}
                {isActive(link.to) && (
                  <span className="absolute bottom-0 left-5 right-5 h-0.5 rounded-full bg-primary" />
                )}
              </Link>
            ))}
          </div>

          {/* Order Online — always visible (desktop + mobile) */}
          <OrderOnlineButton
            url={orderUrl}
            className="h-10 px-4 text-sm sm:h-11 sm:px-6 sm:text-base"
          />

          {/* Mobile hamburger */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger
              render={
                <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open menu" />
              }
            >
              <Menu className="size-6" />
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-surface-lowest p-0">
              <SheetHeader className="border-b border-border p-5">
                <SheetTitle>
                  <img
                    src="/images/logo.svg"
                    alt="Main Street Bread"
                    className="h-8 w-auto"
                  />
                </SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col p-3">
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`rounded-lg px-4 py-3 text-base font-medium no-underline transition-colors ${isActive(link.to)
                        ? 'bg-primary/5 text-primary'
                        : 'text-on-surface-variant hover:bg-surface-high hover:text-on-surface'
                      }`}
                    onClick={() => setOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
                <OrderOnlineButton
                  url={orderUrl}
                  className="mt-3 w-full"
                />
              </nav>
            </SheetContent>
          </Sheet>
          </div>
        </nav>
      </Container>
    </header>
  )
}
